import {
  EntityType,
  IgnoreError,
  SearchQueryOptions,
  useSearchQuery,
} from "core/api";
import { useLoadPagedInfiniteQuery } from "core/components";
import { useMemo } from "react";
import { useLocation } from "react-router";
import useTableSearchStore from "../../Screen/SearchStateStore";

export interface InfinityQuerySearchOptions {
  /** API url */
  apiUrl: string | undefined;
  /** Entity type */
  entityType: EntityType | undefined;
  /** Search query */
  currentSearch: string;
  /** Setting this parameter to **true** forces the query request to be sent
   * to the server even if **currentSearch** is undefined or empty.
   * The default value is **false**, which will prevent sending the request
   * to the server if **currentSearch** is undefined or empty. */
  acceptEmptySearch?: boolean;
  /** Search filters */
  filters: string | string[] | undefined;
  /** If search is disabled and data should be fetched directly */
  searchDisabled: boolean;
  /** Unique search id */
  searchId?: string;
  orderBy: string | undefined;
  page: number | undefined;
  pageSize: number;
  order: "asc" | "desc";
  enabled?: boolean;
  /** Ignore all errors or based on request status codes */
  ignoreError: IgnoreError;
  /** Key for searched table state */
  tableSearchStateKey?: string;
  /** If using extended cache time */
  extendedCacheTime?: boolean;
}

/** Infinity query search */
export function useInfinityQuerySearch({
  apiUrl,
  entityType,
  currentSearch,
  acceptEmptySearch = false,
  filters,
  searchDisabled,
  searchId,
  order,
  orderBy,
  page,
  pageSize,
  enabled,
  ignoreError,
  tableSearchStateKey,
  extendedCacheTime,
}: InfinityQuerySearchOptions) {
  const baseFilterObject: SearchQueryOptions = useMemo(
    () => ({
      apiUrl,
      entityType,
      enabled:
        !!enabled &&
        (currentSearch.length > 0 ||
          !!filters?.length ||
          searchDisabled ||
          acceptEmptySearch),
      search: currentSearch,
      acceptEmptySearch,
      filters: filters ?? [],
      pageNo: 0,
      pageSize,
      orderBy,
      ascending: order === "asc",
      noCache: true,
      searchId,
      ignoreError,
      extendedCacheTime,
    }),
    [
      apiUrl,
      entityType,
      enabled,
      currentSearch,
      acceptEmptySearch,
      filters,
      pageSize,
      orderBy,
      order,
      searchDisabled,
      searchId,
      ignoreError,
      extendedCacheTime,
    ]
  );

  // Fetch stored search values
  const { searchedText, searchedId, searchedFilters } = useTableSearchStore(
    (state) => state.getTableSearchState(tableSearchStateKey ?? "")
  );
  const location = useLocation();
  const showAllSearch = location.state?.showAllSearch ?? false;

  // the part below is needed for when we change sort order, pageSize and orderBy for cached table items
  const filterObject = useMemo(
    () => ({
      ...baseFilterObject,
      ...(searchedText && searchedId && searchedFilters && !showAllSearch
        ? {
            search: searchedText,
            extendedCacheTime: true,
            searchId: searchedId,
            filters: searchedFilters,
          }
        : {}),
    }),
    [baseFilterObject, searchedText, searchedId, searchedFilters]
  );

  return useLoadPagedInfiniteQuery(
    useSearchQuery,
    filterObject,
    page ?? 0,
    pageSize,
    ignoreError
  );
}
